import { AccountingResult, AccountingSystem, Invoice, InvoiceStatus, InvoiceType, isInvoicePaid } from '@jms/model';

import { defineConditionalTag } from '../../shared/define.conditional.tag';
import { defineStatusTag } from '../../shared/define.status.tag';

// Single source of Invoice pill colour. Each pill below feeds BOTH the manifest's board-card tag
// style and the manage surface header's tag deriver, so an Invoice cannot render one colour on the
// card and another in the document header.
//
// Invoice shows five pills because its state is five independent facts: where the document sits in
// its lifecycle, a free-text sub-status the tenant maintains, whether it is a special document type,
// whether it has been settled, and whether the accounting integration accepted it. Only the first is
// an enum-to-tone map; the rest are predicate-gated and appear only when they have something to say.

/** Document lifecycle. `neutral` is also the fallback so unknown/legacy data reads as Draft. */
export const invoiceStatusTag = defineStatusTag<InvoiceStatus, Invoice>({
  [InvoiceStatus.Draft]: 'neutral',
  [InvoiceStatus.Approved]: 'brand',
  [InvoiceStatus.Error]: 'error',
  [InvoiceStatus.Void]: 'neutral',
}, 'neutral');

/**
 * Tenant-maintained sub-status. Untoned by design: it is free-form tenant data with no semantics the
 * palette could speak to, so it renders as a plain neutral pill or not at all.
 */
export const invoiceSubStatusTag = defineConditionalTag<Invoice>(invoice => {

  const name = invoice?.subStatus?.name;
  return name ? { label: name, tone: 'neutral' } : null;
});

/**
 * Document type. A plain Invoice is the unremarkable case and shows no pill at all — only a credit
 * note or a prepayment earns one, because those change how the money reads. Supplier-side variants
 * belong to Bill and are not toned here.
 */
export const invoiceTypeTag = defineConditionalTag<Invoice>(invoice => {

  switch (invoice?.type) {
    case InvoiceType.CreditNote:
      return { label: InvoiceType.CreditNote, tone: 'warning' };
    case InvoiceType.PrepaymentInvoice:
      return { label: InvoiceType.PrepaymentInvoice, tone: 'brand' };
    default:
      return null;
  }
});

/** Settlement. A credit note is "Settled" rather than "Paid" — money went the other way. */
export const invoiceSettlementTag = defineConditionalTag<Invoice>(invoice => {

  if (!isInvoicePaid(invoice)) return null;
  return { label: invoice?.type === InvoiceType.CreditNote ? 'Settled' : 'Paid', tone: 'success' };
});

/**
 * Accounting integration result. Silent until a connected system has actually run: Manual (no
 * QuickBooks / Xero / MYOB mapping) still writes `success: true` on approve, and that is not a sync.
 */
export const invoiceAccountingTag = defineConditionalTag<Invoice>(invoice => {

  const result = invoice?.integration as AccountingResult | null | undefined;
  if (result?.success == null || !isConnectedAccountingIntegration(result)) return null;
  return result.success
    ? { label: 'Synced', tone: 'success' }
    // Warning, not error: the lifecycle Error chip is already error-red and the two pills sit side by side.
    : { label: 'Accounting error', tone: 'warning' };
});

/** A real ledger connection — Manual is the no-op client used when the billing company has none. */
export function isConnectedAccountingIntegration(result: Pick<AccountingResult, 'type'> | null | undefined): boolean {

  return !!result?.type && result.type !== AccountingSystem.Manual;
}
